import React, { useEffect, useState } from 'react'
import { Button, Stack } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'

import { useTicketValidation } from '../hooks/useTicketValidation'
import { useScan } from '../context/scanContext'
import { useTicketHistory } from '../hooks/useTicketHistory'
import { useProductValidation } from '../hooks/useProductValidation'
import TicketHistory from './TicketHistory'
import ProductsSelectionTable from './ProductsSelectionTable'
import VerdictScreen from './scanner/VerdictScreen'
import { playScanResult } from '../services/soundService'
import { Surface, InfoRow } from './ui/Surface'
import { FullScreenLoader } from './ui/States'

const AccessTicketScanResponse = ({ onCancel }) => {
  const { validateTicket, validationResponse } = useTicketValidation()
  const { validateProduct, consumeProducts } = useProductValidation()
  const { getTicketHistory, scanEvents } = useTicketHistory()
  const { currentCode, controlPoint, productList } = useScan()
  const [isLoading, setIsLoading] = useState(true)
  const [showHistory, setShowHistory] = useState(false)
  const [productConsumed, setProductConsumed] = useState(false)

  // Fin de validation : on coupe le chargement et on émet le signal sonore
  // correspondant au verdict, au moment précis où il est connu.
  const finishValidation = (response) => {
    setIsLoading(false)
    playScanResult(Boolean(response?.isValid))
  }

  useEffect(() => {
    if (controlPoint.HasProductsToValidate) {
      validateProduct(currentCode, checkAvailableProducts, () => handleValidateTicket())
    } else {
      validateTicket(currentCode, finishValidation)
    }
  }, [])

  const handleShowHistory = () => {
    setIsLoading(true)
    getTicketHistory(currentCode, () => {
      setShowHistory(true)
      setIsLoading(false)
    })
  }

  const checkAvailableProducts = (p) => {
    if (p.length === 0) {
      validateTicket(currentCode, finishValidation)
    } else {
      setIsLoading(false)
    }
  }

  const handleConsumeProduct = () => {
    setIsLoading(true)
    consumeProducts()
    setProductConsumed(true)
    validateTicket(currentCode, finishValidation)
  }

  const handleValidateTicket = () => {
    setProductConsumed(true)
    validateTicket(currentCode, finishValidation)
  }

  if (isLoading) {
    return <FullScreenLoader label="Vérification du billet" />
  }

  if (showHistory) {
    return <TicketHistory onCancel={() => setShowHistory(false)} scanEvents={scanEvents} />
  }

  // ---- Des produits doivent être remis avant de laisser entrer le porteur ----
  if (productList.length > 0 && !productConsumed) {
    return (
      <VerdictScreen
        status="warning"
        title="Produits à remettre"
        message="Sélectionnez les produits remis au porteur du billet."
        footer={
          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="outlined" color="inherit" size="large" onClick={onCancel}>
              Annuler
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              onClick={handleConsumeProduct}
            >
              Valider
            </Button>
          </Stack>
        }
      >
        <Surface padding={0} sx={{ overflow: 'hidden' }}>
          <ProductsSelectionTable />
        </Surface>
      </VerdictScreen>
    )
  }

  // ---- Verdict d'accès ----
  const isValid = Boolean(validationResponse?.isValid)

  return (
    <VerdictScreen
      status={isValid ? 'valid' : 'invalid'}
      title={isValid ? 'Valide' : 'Invalide'}
      message={validationResponse?.message}
      footer={
        <Stack spacing={1}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            startIcon={<QrCodeScannerIcon />}
            onClick={onCancel}
          >
            Billet suivant
          </Button>
          <Button
            fullWidth
            variant="text"
            color="inherit"
            startIcon={<HistoryIcon />}
            onClick={handleShowHistory}
          >
            Consulter l'historique
          </Button>
        </Stack>
      }
    >
      <Surface>
        <InfoRow label="Type de billet" value={validationResponse?.ticketName} />
        <InfoRow label="Numéro de billet" value={validationResponse?.ticketNumber} emphasis />
        {controlPoint?.Name ? (
          <InfoRow label="Point de contrôle" value={controlPoint.Name} />
        ) : null}
      </Surface>
    </VerdictScreen>
  )
}

export default AccessTicketScanResponse
